/*
	Auto-marcador inteligente (Esteban, 2026-10-13; SPEC_AUTOMARCADOR §4.2). Un solo marcador por pestaña, fuera de React
	(como telefono.ts): la cola, la persona actual, el modo automático y lo ya atendido en esta sesión viven aquí, así CRM y
	WappChat comparten la misma sesión de llamadas al cambiar de pestaña. Quién sigue lo decide el servidor (_dialerNext),
	cada vez que se pide, con la hora actual: un cliente nuevo que cae a media sesión sale arriba sin recargar nada.
*/
import HttpManager from "../../../services/HttpManager";
import { CallCloseBody, DialerItem, DialerQueue, ProspectCatalogs } from "../../../services/v2/prospects.service";
import { errorTexto, nombreCompleto } from "./salesHelpers";
import { aMarcable, FinLlamada, telefono } from "./telefono/telefono";
import { push } from "./push";
import { quienSoy } from "../../../appMovil/contadores";
import { festejo } from "./festejo";

type Escucha = () => void;
export interface Cierre { prospectId: number; fin: FinLlamada | null; aviso?: string | null }
export interface Hecha { id: number; nombre: string; res: string; festejo?: string | null }

const LLAVE_ABIERTO = "wapp_marcador_abierto";
const LLAVE_HECHOS = "wapp_marcador_hechos";
const MX_MS = 6 * 3600000;
const POR_PAGINA = 40;
/** Hoy en hora local (UTC-6): lo atendido en la sesión se olvida al cambiar el día. */
const hoyLocal = () => new Date(Date.now() - MX_MS).toISOString().slice(0, 10);
/** "14:05" hora local, para comparar con la hora de las citas. */
export const ahoraHM = () => new Date(Date.now() - MX_MS).toISOString().slice(11, 16);

const leer = (k: string): string | null => { try { return sessionStorage.getItem(k); } catch { return null; } };
const escribir = (k: string, v: string | null) => { try { if (v === null) sessionStorage.removeItem(k); else sessionStorage.setItem(k, v); } catch { /* sin almacenamiento */ } };

class Marcador {
	campusId: number | null = null;
	/** "me" (mi lista); supervisión puede llamar como otra asesora: "z:<zoho>" o "<userId>". */
	owner = "me";
	cola: DialerQueue | null = null;
	cargando = false;
	error: string | null = null;
	actual: DialerItem | null = null;
	auto = false;
	cierre: Cierre | null = null;
	/**
	 * Ya atendidos o saltados en esta sesión: id → hasta cuándo no se piden (ms). Llamados: 15 min (después manda el
	 * enfriamiento del servidor: un nuevo que no contestó vuelve en 1 h); saltados: el resto del día.
	 */
	private excl: { [id: number]: number } = {};
	get exclude(): number[] { const t = Date.now(); return Object.keys(this.excl).map(Number).filter((id) => this.excl[id] > t); }
	private excluir(id: number, ms: number) { this.excl = { ...this.excl, [id]: Date.now() + ms }; }
	hechas: Hecha[] = [];
	abierto = leer(LLAVE_ABIERTO) === "1";
	/** Cuántos entraron arriba (niveles 0–2) desde la última vez que se vio la lista. */
	nuevos = 0;
	espera: string | null = null;
	aviso: string | null = null;
	catalogs: ProspectCatalogs | null = null;
	private escuchas = new Set<Escucha>();
	private usos = 0;
	private abrirPersona: ((id: number) => void) | null = null;
	private tCiclo: any = null;
	private tPush: any = null;
	private tEspera: any = null;
	private ultimaCarga = 0;
	private offPush: (() => void) | null = null;
	private offConteos: (() => void) | null = null;
	private soltarPush: (() => void) | null = null;
	private pidiendo = false;
	/** Día local de `hechas`: una pestaña abierta de un día para otro empieza la lista de "Hoy en esta pestaña" en cero. */
	private dia = hoyLocal();

	constructor() {
		const h = leer(LLAVE_HECHOS);
		if (h) {
			try {
				const d = JSON.parse(h);
				if (d.dia === hoyLocal()) { this.excl = d.excl || {}; this.hechas = d.hechas || []; }
			} catch { /* basura vieja */ }
		}
		// Al colgar una llamada de la persona actual se abre la tarjeta de cierre (las fichas no la registran: ver atiende()).
		telefono.alTerminar((f) => {
			if (!this.actual || f.prospectId !== this.actual.id) return;
			// Una llamada entrante de esta persona que no se alcanzó a contestar no es un intento nuestro: no se registra
			// (y menos sola a los 5 s). Solo las salientes o las entrantes que sí se atendieron (revisión 14-oct).
			if (f.direction === "in" && !f.answered) return;
			this.cierre = { prospectId: f.prospectId, fin: f };
			this.ponerAbierto(true);
			this.avisar();
		});
	}

	suscribir(fn: Escucha): () => void { this.escuchas.add(fn); return () => { this.escuchas.delete(fn); }; }
	private avisar() { this.escuchas.forEach((fn) => { try { fn(); } catch { /* una pantalla rota no tumba a las demás */ } }); }

	/**
	 * La vista de atrás (CRM o WappChat) se registra al montarse: dice cómo abrir a una persona ahí mismo. Devuelve con qué
	 * soltarlo. Mientras haya una vista: avisos al instante (wapp-push) y recarga cada 90 s con el panel abierto (5 min cerrado).
	 */
	usar(campusId: number, abrirPersona: (id: number) => void): () => void {
		this.usos++;
		this.abrirPersona = abrirPersona;
		if (this.campusId !== campusId) {
			this.campusId = campusId;
			this.cola = null; this.actual = null; this.cierre = null; this.auto = false; this.nuevos = 0; this.catalogs = null;
			this.cargar();
		} else if (!this.cola || Date.now() - this.ultimaCarga > 60000) this.cargar();
		if (!this.offPush) {
			this.soltarPush = push.usar(campusId);
			// Solo los WhatsApp de mis clientes mueven mi fila; los de toda la escuela no (14-oct-2026: 227 recargas/min con la
			// lista cacheada, puro arranque de WAPP). Con cartera ajena o de equipo, cualquiera. "conteos" (el servidor avisa
			// a la dueña cuyos números cambiaron: status, cita, reasignación, reloj) también la mueve.
			this.offPush = push.on("wa", (d) => {
				if (!d || d.estado) return;
				const { yo } = quienSoy();
				const mio = "o" in d && d.o != null && !!yo && Number(d.o) === yo;
				if (this.owner !== "me" || mio) this.recargarPronto();
			});
			this.offConteos = push.on("conteos", () => this.recargarPronto());
		}
		this.ciclo();
		return () => {
			this.usos = Math.max(0, this.usos - 1);
			if (this.abrirPersona === abrirPersona) this.abrirPersona = null;
			if (!this.usos) {
				clearTimeout(this.tCiclo); clearTimeout(this.tPush);
				this.offPush?.(); this.offPush = null;
				this.offConteos?.(); this.offConteos = null;
				this.soltarPush?.(); this.soltarPush = null;
			}
		};
	}

	/** ¿Esta llamada la lleva el marcador? Entonces la ficha no la registra sola ni abre su propio "cómo terminó". */
	atiende(prospectId: number | null | undefined): boolean {
		return !!prospectId && this.usos > 0 && !!this.actual && this.actual.id === prospectId;
	}

	private ciclo() {
		clearTimeout(this.tCiclo);
		if (!this.usos) return;
		this.tCiclo = setTimeout(() => {
			if (document.visibilityState === "visible" && !this.cierre) this.cargar();
			this.ciclo();
		}, this.abierto ? 90000 : 300000);
	}

	/** Un WhatsApp entrante puede cambiar el orden (sube dentro de su nivel) o traer a alguien nuevo: recarga con calma. */
	private recargarPronto() {
		clearTimeout(this.tPush);
		const falta = Math.max(3000, 20000 - (Date.now() - this.ultimaCarga));
		this.tPush = setTimeout(() => { if (!this.cierre) this.cargar(); }, falta);
	}

	/** Algo cambió en una ficha (status, actividad): la lista se pone al día en un momento, sin recargar a cada clic. */
	refrescar() {
		if (!this.usos) return;
		clearTimeout(this.tPush);
		this.tPush = setTimeout(() => { if (!this.cierre) this.cargar(); }, 1500);
	}

	ponerAbierto(v: boolean) {
		if (this.abierto === v) return;
		this.abierto = v;
		escribir(LLAVE_ABIERTO, v ? "1" : null);
		if (v) { this.nuevos = 0; if (Date.now() - this.ultimaCarga > 30000) this.cargar(); }
		this.ciclo();
		this.avisar();
	}

	ponerOwner(owner: string) {
		if (owner === this.owner) return;
		this.owner = owner;
		this.cola = null; this.nuevos = 0;
		if (!this.cierre) { this.actual = null; this.auto = false; }
		this.cargar();
	}

	async cargarCatalogos() {
		if (this.catalogs || !this.campusId) return;
		try { this.catalogs = (await HttpManager.getInstance().prospectsService.catalogs(this.campusId)).data; this.avisar(); } catch { /* sin catálogos: sin selector de cartera */ }
	}
	ponerCatalogos(c: ProspectCatalogs | null) { if (c && c !== this.catalogs) { this.catalogs = c; this.avisar(); } }

	async cargar(mas = false) {
		const campusId = this.campusId;
		if (!campusId) return;
		this.cargando = true; this.avisar();
		try {
			const ya = this.cola?.queue.length || 0;
			const limit = mas ? POR_PAGINA : Math.min(100, Math.max(POR_PAGINA, ya));
			const r = await HttpManager.getInstance().prospectsService.dialerQueue(campusId, this.owner, limit, mas ? ya : 0);
			if (campusId !== this.campusId) return;
			const d = r.data;
			if (mas && this.cola) {
				const vistos = new Set(this.cola.queue.map((x) => x.id));
				this.cola = { ...d, queue: [...this.cola.queue, ...d.queue.filter((x) => !vistos.has(x.id))] };
			} else {
				// "1 nuevo arriba": quien entró a los niveles de arriba y no estaba (no mueve la fila activa).
				if (this.cola) {
					const antes = new Set(this.cola.queue.map((x) => x.id));
					const llegaron = d.queue.filter((x) => x.nivel <= 2 && !antes.has(x.id) && !this.exclude.includes(x.id)).length;
					if (llegaron) this.nuevos += llegaron;
				}
				this.cola = d;
			}
			this.error = null;
			this.ultimaCarga = Date.now();
		} catch (e) { this.error = errorTexto(e); }
		finally { this.cargando = false; this.avisar(); }
	}

	/** Lo que se enseña en la lista: sin lo ya atendido en la sesión ni la persona actual. */
	get pendientes(): DialerItem[] {
		return (this.cola?.queue || []).filter((x) => !this.exclude.includes(x.id) && x.id !== this.actual?.id);
	}
	/** Lo de hoy (niveles 0–6: puestos a mano, citas, nuevos, actividades de hoy y vencidas); 7 y 8 son la reserva. */
	get hoy(): number | null {
		if (!this.cola) return null;
		return this.cola.niveles.filter((n) => n.nivel <= 6).reduce((a, n) => a + n.n, 0);
	}
	/** La píldora se pinta naranja: una cita para llamar ya, alguien puesto a mano, o un nuevo que escribió en la última hora y nadie ha llamado. */
	get urgente(): boolean {
		const hace1h = Date.now() - 3600000;
		return this.pendientes.some((x) => x.nivel <= 1
			|| (x.nivel === 2 && !x.calls_count && !!x.last_inbound_at && new Date(x.last_inbound_at.replace(" ", "T") + (x.last_inbound_at.endsWith("Z") ? "" : "Z")).getTime() >= hace1h));
	}

	abrir(id: number) {
		this.abrirPersona?.(id);
		// En el teléfono el panel es la pantalla completa: se esconde para ver la ficha (la píldora lo vuelve a abrir).
		if (window.matchMedia?.("(max-width: 700px)").matches) this.ponerAbierto(false);
	}

	empezar() { this.auto = true; this.espera = null; this.avisar(); if (!this.actual) this.siguiente(); else if (!telefono.ocupado && !this.cierre) this.llamar(this.actual); }
	pausar() { this.auto = false; clearTimeout(this.tEspera); this.espera = null; this.avisar(); }

	/** Pide al servidor quién sigue ahora, lo abre en la vista de atrás y, en automático, marca. */
	async siguiente() {
		const campusId = this.campusId;
		if (!campusId || this.pidiendo) return;
		this.pidiendo = true; this.espera = null; clearTimeout(this.tEspera);
		try {
			const r = await HttpManager.getInstance().prospectsService.dialerNext(campusId, this.owner, this.exclude);
			// Cambió de plantel mientras llegaba la respuesta: esa persona ya no es de aquí.
			if (campusId !== this.campusId) return;
			const n = r.data.next;
			if (this.cola) this.cola = { ...this.cola, total: r.data.total };
			if (!n) {
				this.actual = null;
				const hasta = r.data.esperaHasta ? new Date(r.data.esperaHasta) : null;
				this.espera = hasta ? "Por ahora nadie. Vuelve a haber desde las " + hasta.toLocaleTimeString("es-MX", { hour: "numeric", minute: "2-digit", hour12: true, timeZone: "America/Mexico_City" }).replace(/\s?([ap])\.?\s?m\.?/i, " $1m") + "."
					: "Terminaste tu lista por ahora. Los que no contestaron vuelven más tarde.";
				// En automático se vuelve a pedir solo cuando vuelva a haber alguien (si es dentro de 3 h).
				if (this.auto && hasta) {
					const ms = hasta.getTime() - Date.now();
					if (ms > 0 && ms < 3 * 3600000) this.tEspera = setTimeout(() => {
						// Ya no está en CRM ni en WappChat: no se marca a nadie a sus espaldas; al volver ve "Seguir".
						if (!this.usos) { this.pausar(); return; }
						if (this.auto && !this.actual) this.siguiente();
					}, ms + 5000);
				}
				return;
			}
			this.actual = n;
			this.cierre = null;
			this.abrir(n.id);
			if (this.auto && this.usos) this.llamar(n);
		} catch (e) { this.error = errorTexto(e); }
		finally { this.pidiendo = false; this.avisar(); }
	}

	/** Llamar a alguien de la lista (o a la persona actual). Si el teléfono de WAPP no conecta, se registra a mano. */
	async llamar(item: DialerItem) {
		if (telefono.ocupado) { this.ponerAviso("Ya estás en una llamada."); return; }
		if (this.cierre && this.actual && this.actual.id !== item.id) { this.ponerAviso("Primero guarda cómo terminó la llamada de " + nombreCompleto(this.actual) + "."); return; }
		if (this.actual?.id !== item.id) { this.actual = item; this.cierre = null; this.abrir(item.id); }
		this.avisar();
		if (!telefono.listo) this.ponerAviso("Conectando el teléfono…", 0);
		const id = await telefono.llamarYa(aMarcable(item.phone_e164), { prospectId: item.id, name: nombreCompleto(item) });
		this.ponerAviso(null);
		if (!id && !telefono.ocupado && this.actual?.id === item.id) {
			this.cierre = { prospectId: item.id, fin: null, aviso: "El teléfono de WAPP no conectó (se sigue reconectando solo). Si marcas desde tu celular, deja aquí cómo terminó." };
			this.ponerAbierto(true);
			this.avisar();
		}
	}

	/** "Registrar": marcó desde el celular (o quiere dejar la nota sin llamar). */
	registrar() { if (this.actual) { this.cierre = { prospectId: this.actual.id, fin: null }; this.avisar(); } }
	cancelarCierre() { this.cierre = null; this.avisar(); }

	/** Guardar y siguiente: una sola llamada a la API (llamada + nota + actividad + status). */
	async guardarCierre(body: CallCloseBody): Promise<void> {
		const campusId = this.campusId, a = this.actual;
		if (!campusId || !a) return;
		const r = await HttpManager.getInstance().prospectsService.callClose(campusId, a.id, body);
		// La ficha abierta de esta persona se pone al día (la tarjeta pudo cerrar su actividad y agendar otra, 2026-10-15).
		window.dispatchEvent(new CustomEvent("wapp:ficha-al-dia", { detail: a.id }));
		const f = r.data?.festejo || null;
		if (f) festejo(f);
		this.excluir(a.id, 15 * 60000);
		this.alDia();
		this.hechas = [{ id: a.id, nombre: nombreCompleto(a), res: body.result, festejo: f }, ...this.hechas].slice(0, 200);
		this.guardarHechos();
		this.borrarNota(a.id);
		this.cierre = null; this.actual = null;
		this.avisar();
		if (this.auto) await this.siguiente();
		this.cargar();
	}

	private saltando = false;
	async saltar() {
		const campusId = this.campusId, a = this.actual;
		if (!campusId || !a || this.saltando) return;   // doble clic: un solo salto
		this.saltando = true;
		try { await HttpManager.getInstance().prospectsService.dialerSkip(campusId, a.id); } catch { /* igual pasa a la siguiente */ }
		const finDia = new Date(hoyLocal() + "T23:59:59Z").getTime() + MX_MS;
		this.excluir(a.id, Math.max(60000, finDia - Date.now()));
		this.alDia();
		this.hechas = [{ id: a.id, nombre: nombreCompleto(a), res: "saltado" }, ...this.hechas].slice(0, 200);
		this.guardarHechos();
		this.cierre = null; this.actual = null;
		this.saltando = false;
		this.avisar();
		if (this.auto) await this.siguiente();
		this.cargar();
	}

	/** Subir (o agregar) a alguien hasta arriba de la lista de hoy. */
	async fijar(id: number): Promise<void> {
		if (!this.campusId) return;
		await HttpManager.getInstance().prospectsService.dialerPin(this.campusId, id, this.owner === "me" ? null : this.owner);
		const resto = { ...this.excl };
		delete resto[id];
		this.excl = resto;
		this.guardarHechos();
		await this.cargar();
	}
	async soltar(id: number): Promise<void> {
		if (!this.campusId) return;
		await HttpManager.getInstance().prospectsService.dialerUnpin(this.campusId, id, this.owner === "me" ? null : this.owner);
		await this.cargar();
	}

	verNuevos() { this.nuevos = 0; this.avisar(); }

	// Nota escrita mientras suena: borrador por persona (se borra al guardar).
	notaDe(id: number): string { return leer("wapp_marcador_nota_" + id) || ""; }
	ponerNota(id: number, t: string) { escribir("wapp_marcador_nota_" + id, t || null); this.avisar(); }
	private borrarNota(id: number) { escribir("wapp_marcador_nota_" + id, null); }

	private tAviso: any = null;
	ponerAviso(t: string | null, ms = 5000) {
		clearTimeout(this.tAviso);
		this.aviso = t;
		this.avisar();
		if (t && ms) this.tAviso = setTimeout(() => { this.aviso = null; this.avisar(); }, ms);
	}

	private alDia() { const h = hoyLocal(); if (h !== this.dia) { this.dia = h; this.hechas = []; } }
	private guardarHechos() { escribir(LLAVE_HECHOS, JSON.stringify({ dia: hoyLocal(), excl: this.excl, hechas: this.hechas.slice(0, 200) })); }
}

export const marcador = new Marcador();
